/**
 * GDPR cookie consent banner — marketing surface only.
 *
 * Pairs with the gtag snippet in `index.html`: that snippet ships with
 * Consent Mode v2 default = `denied` so gtag loads but sets no analytics
 * cookies until the visitor accepts. This component is the UI half —
 * it flips the consent state via `gtag('consent', 'update', ...)` and
 * persists the choice in localStorage so returning visitors don't see
 * the banner again.
 *
 * Why a single banner instead of a CMP (Cookiebot / Klaro / Osano):
 * we only have one analytics cookie family (`_ga*`). A full CMP earns
 * its complexity once we add advertising tags, fingerprinting vendors,
 * or sub-processors that need granular consent — none of which apply
 * to serac.build today.
 *
 * Mount: rendered globally in App.tsx so it appears on every route.
 * Hostname gate inside the component keeps it off dashboard.serac.build
 * (which doesn't load gtag at all — see index.html host check).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const CONSENT_KEY = "serac-cookie-consent";

type ConsentState = "accepted" | "declined" | null;

function readConsent(): ConsentState {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === "accepted" || v === "declined" ? v : null;
  } catch {
    return null;
  }
}

function writeConsent(state: Exclude<ConsentState, null>) {
  try {
    localStorage.setItem(CONSENT_KEY, state);
  } catch {
    /* sandboxed / quota — banner just reappears next visit */
  }
}

function applyConsentToGtag(state: Exclude<ConsentState, null>) {
  const w = window as typeof window & { gtag?: (...args: unknown[]) => void };
  if (typeof w.gtag !== "function") return;
  w.gtag("consent", "update", {
    analytics_storage: state === "accepted" ? "granted" : "denied",
  });
}

function isMarketingHost(): boolean {
  if (typeof location === "undefined") return false;
  return location.hostname === "serac.build" || location.hostname === "www.serac.build";
}

export default function CookieBanner() {
  const { t } = useTranslation("common");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isMarketingHost()) return;
    if (readConsent() !== null) return;
    // Defer reveal one frame so the banner doesn't slam the LCP.
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  if (!visible) return null;

  const onAccept = () => {
    writeConsent("accepted");
    applyConsentToGtag("accepted");
    setVisible(false);
  };

  const onDecline = () => {
    writeConsent("declined");
    applyConsentToGtag("declined");
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-message"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        padding: "16px",
        background: "var(--bg)",
        borderTop: "1px solid var(--border)",
        boxShadow: "0 -8px 32px rgba(0, 0, 0, 0.16)",
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 16,
        }}
      >
        <div style={{ flex: "1 1 320px", minWidth: 0 }}>
          <div
            id="cookie-banner-title"
            style={{ fontSize: 14, fontWeight: 600, color: "var(--fg)", marginBottom: 4 }}
          >
            {t("cookies.banner.title", "Cookies on serac.build")}
          </div>
          <div
            id="cookie-banner-message"
            style={{ fontSize: 13, color: "var(--fg-muted)", lineHeight: 1.5 }}
          >
            {t(
              "cookies.banner.message",
              "We use Google Analytics to understand how the marketing site is used. No tracking until you choose. ",
            )}
            <Link
              to="/legal/cookies"
              style={{ color: "var(--fg)", textDecoration: "underline", textUnderlineOffset: 2 }}
            >
              {t("cookies.banner.learnMore", "Read our cookie policy")}
            </Link>
            .
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button
            type="button"
            onClick={onDecline}
            style={{
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 500,
              color: "var(--fg-muted)",
              background: "transparent",
              border: "1px solid var(--border)",
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            {t("cookies.banner.decline", "Decline")}
          </button>
          <button
            type="button"
            onClick={onAccept}
            style={{
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 600,
              color: "var(--bg)",
              background: "var(--fg)",
              border: "1px solid var(--fg)",
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            {t("cookies.banner.accept", "Accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
